import _ from 'lodash'
import {
  canBookRoomConfiguration,
  getRoomConfigurationSortOrder,
  isNewStudioType
} from '../../logic/roomLogic'
import { roomConfigurations } from '../../constants/roomConstants'
import { isAuthenticatedSelector } from '../selectors/authSelector'
import { change as changeFieldValue, getValues } from 'redux-form'
import {
  roomSearchResultsSelector,
  searchResultsForListingSelector
} from './searchResultsModule'
import { getRoom } from '../selectors/objectSelectors'

export const roomSelectionFormSelector = state => state.form.roomSelection
export const roomListingFieldSelector = (state, listingIndex) =>
  roomSelectionFormSelector(state).listings[listingIndex]
export const roomFieldSelector = (state, listingIndex, roomIndex) =>
  roomListingFieldSelector(state, listingIndex).rooms[roomIndex]

/**
 * Determines whether to auto-assign room or not. We auto-assign if:
 * - The user is a guest user, and there isn't a room selected, and
 *   they choose a room type without configuration field, or they
 *   choose a room with a configuration field and configuration is selected.
 *   Or configuration is different from currently selected room.
 *
 * @param state
 * @param listingIndex
 * @param roomIndex
 * @returns {boolean}
 */
export const shouldAutoAssignRoomSelector = (
  state,
  listingIndex,
  roomIndex
) => {
  const isAuthenticated = isAuthenticatedSelector(state)
  const roomField = roomFieldSelector(state, listingIndex, roomIndex)
  const roomFieldValues = getValues(roomField)
  const searchResult = roomSearchResultsSelector(state, listingIndex)

  const roomId = roomFieldValues.roomId
  const hasRoomSelected = !!roomId
  const roomConfiguration = roomFieldValues.roomConfiguration

  return (
    !isAuthenticated &&
    (isNewStudioType(searchResult.type)
      ? (!hasRoomSelected && roomConfiguration) ||
        (hasRoomSelected &&
          roomConfiguration &&
          getRoom(state, roomId).roomConfiguration !== roomConfiguration)
      : !hasRoomSelected)
  )
}

export const shouldResetRoomSelector = (state, listingIndex, roomIndex) => {
  const roomField = roomFieldSelector(state, listingIndex, roomIndex)
  const roomFieldValues = getValues(roomField)
  const searchResult = roomSearchResultsSelector(state, listingIndex)

  const roomId = roomFieldValues.roomId
  const room = getRoom(state, roomId)
  const roomConfiguration = roomFieldValues.roomConfiguration
  return (
    roomId &&
    (isNewStudioType(searchResult.type) &&
      (!roomConfiguration || roomConfiguration !== room.roomConfiguration))
  )
}

export const getNumRooms = roomListingField => roomListingField.numRooms.value
export const getNumGuests = roomField => roomField.numGuests.value
export const getNumAdults = getNumGuests
export const getNumChildren = roomField => roomField.numChildren.value
export const getRoomId = roomField => roomField.roomId.value
export const getRoomConfiguration = roomField =>
  roomField.roomConfiguration.value

export const getNumGuestsFromRoomField = roomField => {
  let guestCount = roomField.numGuests.value
  if (roomField.numChildren && roomField.numChildren.value) {
    guestCount += roomField.numChildren.value
  }
  return guestCount
}

export const getSelectedRoomIds = roomListingField =>
  _.map(roomListingField.rooms, room => room.roomId.value)

const getSelectableRooms = (rooms, allSelectedRooms, selectedRoom) =>
  _.filter(
    rooms,
    room => !_.includes(allSelectedRooms, room.id) || room.id === selectedRoom
  )

/**
 * If no room configurations are available, then we make the
 * next lowest-capacity configuration available so single visitors or
 * small groups could book
 * @param roomConfigurations
 * @param numAdults
 * @param numChildren
 */
function ensureOneConfigurationIsAvailable (
  roomConfigurations,
  numAdults,
  numChildren
) {
  if (!_.some(roomConfigurations, 'canBook')) {
    const firstWithCapacity = _.findIndex(
      roomConfigurations,
      ({ rooms, roomConfiguration }) => {
        const { reasonCode } = canBookRoomConfiguration(
          roomConfiguration,
          numAdults,
          numChildren
        )
        return rooms.length > 0 && reasonCode !== 'max'
      }
    )

    if (firstWithCapacity !== -1) {
      roomConfigurations[firstWithCapacity].canBook = true
      roomConfigurations[firstWithCapacity].disabled = false
    }
  }

  return roomConfigurations
}

export function getRoomConfigurations (
  rooms,
  numAdults,
  numChildren,
  allSelectedRooms,
  selectedRoom
) {
  const roomsByConfiguration = _(rooms)
    .groupBy('roomConfiguration')
    .value()

  const configurationValues = _(roomConfigurations)
    .mapValues(roomConfiguration => {
      const rooms = getSelectableRooms(
        roomsByConfiguration[roomConfiguration] || [],
        allSelectedRooms,
        selectedRoom
      )

      const { canBook, reason } = canBookRoomConfiguration(
        roomConfiguration,
        numAdults,
        numChildren
      )

      const areRoomsAvailable = rooms && rooms.length > 0
      const canBookConfiguration = canBook && areRoomsAvailable

      return {
        rooms,
        roomConfiguration,
        value: roomConfiguration,
        canBook: canBookConfiguration,
        reason: areRoomsAvailable ? reason : 'No Rooms Available',
        disabled: !canBookConfiguration
      }
    })
    .sortBy(({ roomConfiguration }) =>
      getRoomConfigurationSortOrder(roomConfiguration)
    )
    .value()

  return ensureOneConfigurationIsAvailable(
    configurationValues,
    numAdults,
    numChildren
  )
}

function getRoomPickerOptions ({
  roomListingField,
  roomField,
  rooms,
  roomConfiguration
}) {
  const selectedRoom = roomField.roomId.value
  let allSelectedRooms = getSelectedRoomIds(roomListingField)
  let selectableRooms = getSelectableRooms(
    rooms,
    allSelectedRooms,
    selectedRoom
  )

  let options = _(selectableRooms)
    .filter(room => {
      return roomConfiguration
        ? room.roomConfiguration === roomConfiguration
        : true
    })
    // Note that this works only with our custom optionRenderer.
    // If you use the default renderer, remember to set the `label` property
    .map(room => ({
      value: room.id,
      name: room.name,
      isClean: room.isClean
    }))
    .sortBy('name')
    .value()

  return options
}

export function roomPickerOptionsSelector (state, listingIndex, roomIndex) {
  return getRoomPickerOptions({
    roomListingField: roomListingFieldSelector(state, listingIndex),
    roomField: roomFieldSelector(state, listingIndex, roomIndex),
    rooms: searchResultsForListingSelector(state, listingIndex),
    roomConfiguration: roomFieldSelector(state, listingIndex, roomIndex)
      .roomConfiguration.value
  })
}

export function getNumGuestsFields (
  showConfigurationPicker,
  roomField,
  onRoomConfigurationChange
) {
  const wrapOnChange = field => ({
    ...field,
    onChange: value => {
      field.onChange(value)

      // Reset room configuration value if configuration is no longer available
      const { canBook } = canBookRoomConfiguration(
        getRoomConfiguration(roomField),
        getNumAdults(roomField),
        getNumChildren(roomField)
      )
      if (!canBook) {
        onRoomConfigurationChange('')
      }
    }
  })
  return showConfigurationPicker
    ? {
      numAdultsField: wrapOnChange(roomField.numGuests),
      numChildrenField: wrapOnChange(roomField.numChildren)
    }
    : { numGuestsField: roomField.numGuests }
}

export const autoAssignRoomIfNeeded = (listingIndex, roomIndex) => (dispatch, getState) => {
  const state = getState()
  const isAuthenticated = isAuthenticatedSelector(state)
  if (isAuthenticated) return

  const setRoomId = value =>
    dispatch(
      changeFieldValue(
        'roomSelection',
        `listings[${listingIndex}].rooms[${roomIndex}].roomId`,
        value
      )
    )

  if (shouldAutoAssignRoomSelector(state, listingIndex, roomIndex)) {
    const dropdownOptions = roomPickerOptionsSelector(
      state,
      listingIndex,
      roomIndex
    )
    setRoomId(dropdownOptions[0].value)
  } else if (shouldResetRoomSelector(state, listingIndex, roomIndex)) {
    setRoomId(null)
  }
}



// WEBPACK FOOTER //
// ./src/redux/modules/roomSelectionFormModule.js